import { autoUpdate, flip, shift, useFloating } from '@floating-ui/react-dom'
import { PageLocaleEnum, PageLocaleNames } from 'common/enums/PageLocaleEnum'
import { useFloatHover } from 'common/hooks/useFloatHover'
import React from 'react'
import { CommonWebsiteLanguageSwitcherInterface } from 'src/shared/types/entities/website-language-switcher-interface'
import { ChevronDownIcon } from './ui/chevron-down-icon'
import { WebsiteLanguageSwitcherCurrentLangArrowUi } from './ui/website-language-switcher-current-lang-arrow-ui'
import { WebsiteLanguageSwitcherCurrentLangUi } from './ui/website-language-switcher-current-lang-ui'
import { WebsiteLanguageSwitcherDropdownItemUi } from './ui/website-language-switcher-dropdown-item-ui'
import { WebsiteLanguageSwitcherDropdownUi } from './ui/website-language-switcher-dropdown-ui'
import { WebsiteLanguageSwitcherIconWrapperUi } from './ui/website-language-switcher-icon-wrapper-ui'
import { WebsiteLanguageSwitcherUi } from './ui/website-language-switcher-ui'
import { removeLocalePrefixFromPath } from './utils/remove-locale-prefix-from-path'
import { WebsiteLanguageSwitcherIcon } from './website-language-switcher-icon'

interface CommonWebsiteLanguageSwitcherProps
  extends CommonWebsiteLanguageSwitcherInterface {
  currentLanguage: PageLocaleEnum
  websiteLocales: PageLocaleEnum[]
  websiteDefaultLocale?: PageLocaleEnum
  skipLinksGeneration?: boolean
}

export const CommonWebsiteLanguageSwitcher = ({
  currentLanguage,
  excludedLanguages,
  websiteLocales,
  websiteDefaultLocale,
  skipLinksGeneration,
  ...props
}: CommonWebsiteLanguageSwitcherProps) => {
  const { show, delayOpen, delayClose } = useFloatHover()
  const { refs, floatingStyles } = useFloating<HTMLDivElement>({
    placement: 'bottom',
    middleware: [flip(), shift()],
    whileElementsMounted: autoUpdate,
    open: show,
    strategy: 'absolute',
  })

  const getUrl = (locale: string): string => {
    if (skipLinksGeneration) {
      return ''
    }
    const pathnameWithoutLocale = removeLocalePrefixFromPath(
      window.location.pathname,
    )

    const suffix = pathnameWithoutLocale ? `/${pathnameWithoutLocale}` : ''

    return websiteDefaultLocale === locale
      ? suffix || '/'
      : `/${locale}${suffix}`
  }

  const websiteLocalesFiltered =
    websiteLocales
      ?.filter(countryKey => countryKey !== currentLanguage)
      .filter(countryKey => !excludedLanguages?.includes(countryKey)) ?? []
  const isWebsiteLocales = websiteLocalesFiltered.length !== 0

  return (
    <WebsiteLanguageSwitcherUi>
      <WebsiteLanguageSwitcherCurrentLangUi
        ref={refs.setReference}
        padding={props.padding}
        mobilePadding={props.mobilePadding}
        fontSize={props.fontSize}
        color={props.textColor}
        fontFamily={props.fontFamily}
        fontWeight={props.fontWeight}
        fontStyle={props.fontStyle}
        lineHeight={props.lineHeight}
        mobileColor={props.mobileColor}
        mobileFontSize={props.mobileFontSize}
        mobileLineHeight={props.mobileLineHeight}
        letterSpacing={props.letterSpacing}
        onMouseEnter={delayOpen}
        onMouseLeave={delayClose}
      >
        <WebsiteLanguageSwitcherIconWrapperUi>
          <WebsiteLanguageSwitcherIcon
            locale={currentLanguage}
            style={{ height: '1.6429em', width: '1.5714em' }}
          />
        </WebsiteLanguageSwitcherIconWrapperUi>
        {PageLocaleNames[currentLanguage]}
        {isWebsiteLocales && (
          <WebsiteLanguageSwitcherCurrentLangArrowUi show={show}>
            <ChevronDownIcon
              style={{ height: '1.142857em', width: '1.142857em' }}
            />
          </WebsiteLanguageSwitcherCurrentLangArrowUi>
        )}
      </WebsiteLanguageSwitcherCurrentLangUi>
      {show && isWebsiteLocales && (
        <WebsiteLanguageSwitcherDropdownUi
          ref={refs.setFloating}
          onMouseEnter={delayOpen}
          onMouseLeave={delayClose}
          isOpened={show}
          style={floatingStyles}
          fontSize={props.fontSize}
          mobileFontSize={props.mobileFontSize}
        >
          {websiteLocalesFiltered.map(lang => (
            <WebsiteLanguageSwitcherDropdownItemUi
              key={lang}
              href={getUrl(lang)}
              fontSize={props.fontSize}
              fontFamily={props.fontFamily}
              fontWeight={props.fontWeight}
              fontStyle={props.fontStyle}
              lineHeight={props.lineHeight}
              mobileFontSize={props.mobileFontSize}
              mobileLineHeight={props.mobileLineHeight}
              letterSpacing={props.letterSpacing}
            >
              <WebsiteLanguageSwitcherIconWrapperUi>
                <WebsiteLanguageSwitcherIcon
                  locale={lang}
                  style={{ height: '1.6429em', width: '1.5714em' }}
                />
              </WebsiteLanguageSwitcherIconWrapperUi>
              {PageLocaleNames[lang]}
            </WebsiteLanguageSwitcherDropdownItemUi>
          ))}
        </WebsiteLanguageSwitcherDropdownUi>
      )}
    </WebsiteLanguageSwitcherUi>
  )
}
